/**
 * /growth/ref-links/:id — edit form. Cycle 15.
 *
 * All fields are editable except hits (read-only counter). Save patches
 * the row; the URL recomputes if igUsername changes (it doesn't here,
 * since igAccountId is read-only post-create — moving a ref link
 * across IG accounts breaks the link semantics).
 */
import { useEffect, useState, type FormEvent } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { api } from "../../../../lib/api.js";

const API_BASE = import.meta.env.VITE_API_URL ?? "";

interface FlowRow {
  id: string;
  name: string;
  publishedVersionId: string | null;
}

interface RefLink {
  id: string;
  name: string;
  description: string | null;
  payload: string;
  enabled: boolean;
  hits: number;
  flowId: string | null;
  tagsToAdd: string[];
  igAccountId: string;
  igUsername: string;
  url: string;
}

export const Route = createFileRoute("/_app/growth/ref-links/$refLinkId")({
  component: EditRefLinkPage,
});

function EditRefLinkPage() {
  const { refLinkId } = Route.useParams();
  const navigate = useNavigate();
  const [link, setLink] = useState<RefLink | null>(null);
  const [flows, setFlows] = useState<FlowRow[]>([]);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [payload, setPayload] = useState("");
  const [flowId, setFlowId] = useState("");
  const [tagsInput, setTagsInput] = useState("");
  const [enabled, setEnabled] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    void Promise.all([
      api.get<{ refLink: RefLink }>(`/ref-links/${refLinkId}`),
      api.get<{ flows: FlowRow[] }>("/flows"),
    ])
      .then(([lr, fr]) => {
        const l = lr.data.refLink;
        setLink(l);
        setName(l.name);
        setDescription(l.description ?? "");
        setPayload(l.payload);
        setFlowId(l.flowId ?? "");
        setTagsInput((l.tagsToAdd ?? []).join(", "));
        setEnabled(l.enabled);
        setFlows(fr.data.flows.filter((f) => f.publishedVersionId));
      })
      .catch(() => setError("Failed to load"));
  }, [refLinkId]);

  async function save(e: FormEvent) {
    e.preventDefault();
    if (!link || busy) return;
    setBusy(true);
    setError(null);
    try {
      const tagsToAdd = tagsInput
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean);
      const r = await api.patch<{ refLink: RefLink }>(`/ref-links/${refLinkId}`, {
        name: name.trim(),
        description: description.trim() || null,
        payload: payload.trim(),
        flowId: flowId || null,
        tagsToAdd,
        enabled,
      });
      // Refetch to recompute URL (payload may have changed).
      const fresh = await api.get<{ refLink: RefLink }>(`/ref-links/${refLinkId}`);
      setLink(fresh.data.refLink);
      void r;
    } catch (err) {
      const msg = (err as { response?: { data?: { message?: string } } }).response
        ?.data?.message;
      setError(msg ?? "Failed to save");
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!confirm("Delete this ref link?")) return;
    try {
      await api.delete(`/ref-links/${refLinkId}`);
      void navigate({ to: "/growth/ref-links" });
    } catch (err) {
      const msg = (err as { response?: { data?: { message?: string } } }).response
        ?.data?.message;
      setError(msg ?? "Failed to delete");
    }
  }

  async function copyUrl() {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignore
    }
  }

  if (!link) {
    return (
      <section style={{ maxWidth: 600, margin: "0 auto" }}>
        <p>{error ?? "Loading…"}</p>
      </section>
    );
  }

  return (
    <section style={{ maxWidth: 600, margin: "0 auto" }}>
      <Link to="/growth/ref-links" style={{ fontSize: 13 }}>
        ← Ref links
      </Link>
      <h1 style={{ margin: "4px 0 4px", fontSize: 28 }}>{link.name}</h1>
      <div style={{ color: "var(--muted)", fontSize: 13, marginBottom: 16 }}>
        @{link.igUsername} · {link.hits} hit{link.hits === 1 ? "" : "s"}
      </div>

      <div className="brut-card" style={{ padding: 14, marginBottom: 16, display: "grid", gap: 8 }}>
        <code style={{ background: "var(--bg-soft)", padding: "6px 8px", fontSize: 12, overflow: "auto" }}>
          {link.url}
        </code>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={() => void copyUrl()} className="brut-button is-secondary" style={{ padding: "4px 10px", fontSize: 12 }}>
            {copied ? "Copied!" : "Copy URL"}
          </button>
          <a
            href={`${API_BASE}/ref-links/${link.id}/qr`}
            className="brut-button is-secondary"
            style={{ padding: "4px 10px", fontSize: 12, textDecoration: "none" }}
          >
            QR PNG
          </a>
          <a
            href={`${API_BASE}/ref-links/${link.id}/qr?format=svg`}
            className="brut-button is-secondary"
            style={{ padding: "4px 10px", fontSize: 12, textDecoration: "none" }}
          >
            QR SVG
          </a>
        </div>
      </div>

      <form onSubmit={save} className="brut-card" style={{ padding: 14, display: "grid", gap: 10 }}>
        <label className="brut-label">Name</label>
        <input value={name} onChange={(e) => setName(e.target.value)} className="brut-input" maxLength={120} required />
        <label className="brut-label">Description</label>
        <input value={description} onChange={(e) => setDescription(e.target.value)} className="brut-input" maxLength={500} />
        <label className="brut-label">Payload</label>
        <input
          value={payload}
          onChange={(e) => setPayload(e.target.value)}
          className="brut-input"
          pattern="[a-zA-Z0-9_-]{1,64}"
          maxLength={64}
          required
        />
        <label className="brut-label">Start flow</label>
        <select value={flowId} onChange={(e) => setFlowId(e.target.value)} className="brut-input">
          <option value="">(none)</option>
          {flows.map((f) => (
            <option key={f.id} value={f.id}>{f.name}</option>
          ))}
        </select>
        <label className="brut-label">Tags to add (comma-separated)</label>
        <input value={tagsInput} onChange={(e) => setTagsInput(e.target.value)} className="brut-input" />
        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
          Enabled
        </label>

        {error ? <div style={{ color: "#b40000", fontSize: 13 }}>{error}</div> : null}

        <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
          <button type="submit" disabled={busy} className="brut-button">
            {busy ? "Saving…" : "Save"}
          </button>
          <button type="button" onClick={() => void remove()} className="brut-button is-danger" style={{ marginLeft: "auto" }}>
            Delete
          </button>
        </div>
      </form>
    </section>
  );
}
